import { luminesceExtraCards } from '~/utils/battle'
import { TUNING } from '~/config/tuning'
import { FALLBACK_REWARD_ID, REWARD_DEFS, rewardAvailable } from '~/data/rewards'
import { RELIC_POOL, relicAvailable } from '~/data/relics'
import { KEEPSAKE_RELIC } from '~/data/keepsakes'
import { rarityOf } from '~/data/rarity'
// Explicit (not auto-) imports so the rarity simulation can bundle this
// module outside of Nuxt.
import { makeId, weightedPick } from '~/utils/random'
import { drawThroughCycle } from '~/utils/offerCycle'
import { ownedSynergyIds, synergyWeightMult } from '~/data/synergies'
import { activeWeeklyMod } from '~/data/weeklyMods'
import type { NodeType, Rarity, RewardOffer, RewardPool, RunState } from '~/types/game'

/**
 * Reward stages by node type (design notes):
 * - regular battles → 1 regular stage
 * - elite and act bosses → regular stage, then relic stage
 * - treasure (chest) → relic stage only
 * - rest → healing stage only
 * Mystery resolves its own pools at reveal time in confirmNode.
 */
export function poolsForNode(type: NodeType): RewardPool[] {
  switch (type) {
    case 'elite':
    case 'boss':
      return ['regular', 'relic']
    case 'chest':
      return ['relic']
    case 'rest':
      return ['healing']
    case 'swarm':
    case 'mixed':
    case 'titan':
    case 'ambush':
      return ['regular']
    default:
      return []
  }
}

const RARITY_RANK: Record<Rarity, number> = { Common: 0, Uncommon: 1, Rare: 2, Epic: 3, Legendary: 4 }

/**
 * Milestone nodes guarantee reward quality (per Javi, 2026-07-04 — the
 * first place simulated rarity affects drops, not just borders): act
 * bosses offer Epic-or-better in BOTH stages; elites draw Rare-or-better
 * relics. Everything else keeps the full random pool.
 */
export function rarityFloor(type: NodeType | null | undefined, pool: RewardPool): Rarity | null {
  if (type === 'boss') return 'Epic'
  if (type === 'elite' && pool === 'relic') return 'Rare'
  return null
}

interface Candidate {
  defId: string
  weight: number
}

/**
 * Rarity drives drop odds EVERYWHERE (v0.77, per Javi — it was cosmetic
 * outside the milestone floors). STRICT curve (v0.80, per Javi — the
 * gentle 100/75/55/35/20 let three Legendaries land back-to-back in
 * act 1): top tiers are genuinely scarce now. Floors (boss/elite/puzzle)
 * filter the pool first; these odds then bias WITHIN the survivors.
 */
const RARITY_WEIGHTS: Record<Rarity, number> = {
  Common: 100,
  Uncommon: 60,
  Rare: 30,
  Epic: 12,
  Legendary: 5,
}

function candidatesFor(pool: RewardPool, run: RunState): Candidate[] {
  // 🧩 Synergy nudge (v2.38, per Javi): offers lean LIGHTLY (×1.6)
  // toward items that pair with what the run already owns, and away
  // (×0.75) from redundant ones — never a lock, just a thumb on the
  // scale. Table: data/synergies.ts.
  const owned = ownedSynergyIds(run)
  // 🗓️⚗️ v5.66: a weekly law can pull offers that would be dead picks
  // under it (Fresh Legs pulls the travel relics, per Javi).
  const excluded = new Set(activeWeeklyMod(run)?.excludeOffers ?? [])
  if (pool === 'relic') {
    return RELIC_POOL
      .filter(r => relicAvailable(r, run.ascension))
      .filter(r => !run.relics.includes(r.id))
      .filter(r => !excluded.has(r.id))
      .filter(r => !r.excludesBonus || run.bonusId !== r.excludesBonus)
      .map(r => ({ defId: r.id, weight: RARITY_WEIGHTS[rarityOf(r.id)] * synergyWeightMult(r.id, owned) }))
  }
  return REWARD_DEFS
    .filter(def => rewardAvailable(def, run.ascension))
    .filter(def => def.pool === pool)
    .filter(def => !excluded.has(def.id))
    .filter(def => !def.canOffer || def.canOffer(run))
    .map(def => ({ defId: def.id, weight: RARITY_WEIGHTS[rarityOf(def.id)] * synergyWeightMult(def.id, owned) }))
}

/** Draws up to `count` weighted distinct picks from a candidate list. */
function drawCandidates(candidates: Candidate[], count: number): Candidate[] {
  const picks: Candidate[] = []
  const remaining = [...candidates]
  while (picks.length < count && remaining.length > 0) {
    const chosen = weightedPick(remaining.map(c => [c, c.weight] as const))
    remaining.splice(remaining.indexOf(chosen), 1)
    picks.push(chosen)
  }
  return picks
}
function drawPicks(candidates: Candidate[], count: number, pool: RewardPool): RewardOffer[] {
  return drawCandidates(candidates, count).map(c => ({ id: makeId('rw'), defId: c.defId, pool }))
}

/** Max-HP rest rewards vs pure heals vs cures, split by tag. */
function isMaxHpReward(defId: string): boolean {
  return REWARD_DEFS.find(d => d.id === defId)?.tags.includes('max_hp') ?? false
}
function isCureReward(defId: string): boolean {
  return REWARD_DEFS.find(d => d.id === defId)?.tags.includes('cleanse') ?? false
}

/**
 * Up to 3 distinct valid offers; fewer when the pool runs dry, and the
 * fallback (Group Practice XP) when nothing at all is valid. A rarity
 * floor (boss/elite nodes) filters the pool but never empties it — if
 * nothing clears the floor, the full pool applies.
 *
 * Rest nodes offer one pure heal + one max-HP option (v0.49; a dry
 * subpool backfills from the other), PLUS a third card from the CURE
 * pool whenever one is offerable (v0.78 — ascension runs with injuries).
 */
export function generateOffers(pool: RewardPool, run: RunState, minRarity?: Rarity | null, nodeType?: string, reroll?: { count: number; exclude: readonly string[] }): RewardOffer[] {
  // ✨ Luminesce (v3.01): an armed-and-won charge widens this pick.
  // Refreshing an existing offer keeps its earned width and never consumes
  // another Luminesce charge or reissues a pinned keepsake.
  const extraCards = reroll ? Math.max(0, reroll.count - TUNING.rewards.optionCount) : luminesceExtraCards(run, nodeType)
  const optionCount = reroll?.count ?? TUNING.rewards.optionCount + extraCards
  let candidates = candidatesFor(pool, run)
  if (minRarity) {
    const floored = candidates.filter(c => RARITY_RANK[rarityOf(c.defId)] >= RARITY_RANK[minRarity])
    if (floored.length > 0) candidates = floored
  }
  if (reroll) {
    const fresh = candidates.filter(c => !reroll.exclude.includes(c.defId))
    if (fresh.length >= Math.min(reroll.count, candidates.length)) candidates = fresh
  }
  let picks: RewardOffer[]
  if (pool === 'healing') {
    const cures = candidates.filter(c => isCureReward(c.defId))
    const pureHeals = candidates.filter(c => !isMaxHpReward(c.defId) && !isCureReward(c.defId))
    const maxHp = candidates.filter(c => isMaxHpReward(c.defId))
    picks = [...drawPicks(pureHeals, 1, pool), ...drawPicks(maxHp, 1, pool)]
    if (picks.length < 2) {
      const usedIds = new Set(picks.map(p => p.defId))
      const rest = candidates.filter(c => !usedIds.has(c.defId) && !isCureReward(c.defId))
      picks.push(...drawPicks(rest, 2 - picks.length, pool))
    }
    // The cure slot: a third card, present only when a cure is offerable.
    picks.push(...drawPicks(cures, 1, pool))
    if (extraCards > 0) {
      const usedIds = new Set(picks.map(p => p.defId))
      picks.push(...drawPicks(candidates.filter(c => !usedIds.has(c.defId)), extraCards, pool))
    }
  } else if (pool === 'relic') {
    // 🔁 v5.17 (per Javi): declined relics sit out until the whole pool
    // has been offered once around. A rarity FLOOR (boss/elite) narrows
    // the bag to its top shelf — running that slice dry must not wipe
    // the whole cycle, so floored draws refill without resetting it.
    const byId = new Map(candidates.map(c => [c.defId, c]))
    picks = drawThroughCycle(
      run, 'relic', candidates.map(c => c.defId), optionCount,
      (ids, n) => drawCandidates(ids.map(id => byId.get(id)!), n).map(c => c.defId),
      !minRarity,
    ).map(defId => ({ id: makeId('rw'), defId, pool }))
  } else {
    picks = drawPicks(candidates, TUNING.rewards.optionCount + extraCards, pool)
  }
  if (picks.length === 0) {
    picks.push({ id: makeId('rw'), defId: FALLBACK_REWARD_ID, pool: 'regular' })
  }
  // 🎗️ v6.54 (per Javi): the keepsake rides the run's FIRST relic offer
  // as a fourth card - once, never in a seeded run, never in the cycle.
  // 🎗️ v7.48 (per Javi): never in the gauntlet either (a save from before still carries the flags).
  if (!reroll && pool === 'relic' && run.mode !== 'daily' && !run.gauntlet && run.keepsakes?.relic && !run.keepsakeOffered?.relic
    && !run.relics.includes(KEEPSAKE_RELIC)) {
    picks.push({ id: makeId('rw'), defId: KEEPSAKE_RELIC, pool })
    run.keepsakeOffered = { ...run.keepsakeOffered, relic: true }
  }
  return picks
}
